import { Button, DialogState, StandardDialog } from '@roc/ui';
import { observer } from 'mobx-react';
import { useGeneralTasksStore } from '../../hooks/useGeneralTasksStore';
import { ChangeEvent, useEffect, useState } from 'react';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { Box, CircularProgress, Grid, TextField } from '@material-ui/core';
import { TaskAssigneeField } from '../editTask/taskAssigneeField';
import { DocumentName } from '@roc/feature-documents';
import { TaskReasonField } from '../editTask/taskReasonField';
import { isNotBlank } from '@roc/feature-utils';
import { useLoanStore } from '@roc/feature-loans';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      width: '100%',
    },
    badge: {
      marginRight: '5px',
    },
    dialogPaper: {
      overflow: 'visible',
      margin: 'auto',
    },
    dialogContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  }),
);

type AssignToDialogProps = {
  open: boolean;
  loanId: number;
  taskId: number;
  taskName?: DocumentName;
  readOnly?: boolean;
  propertyId?: number;
  primaryReason?: string;
  hasComments?: boolean;
  showExtraFields?: boolean;
  handleClose: () => void;
};

export const AssignToDialog = observer(({ open, loanId, taskId, taskName, readOnly, propertyId, primaryReason, hasComments = true, showExtraFields = true, handleClose }: AssignToDialogProps) => {
  const { generalTasksStore } = useGeneralTasksStore();
  const { editTaskStore, assigneeOptions } = generalTasksStore;
  const internalAssigneeOptions = (assigneeOptions || []).filter(option => option.isRoleInternal);
  const classes = useStyles();

  const { loanStore } = useLoanStore();

  const [comment, setComments] = useState<string>('');
  const [errorText, setErrorText] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (open) {
      setIsLoading(true);
      Promise.all([
        generalTasksStore.fetchUserInfoAndAssigneeOptions(loanId, null, taskId, readOnly, taskName, true),
        editTaskStore.reloadTaskById(taskId, true),
        ...(readOnly && showExtraFields && taskName === DocumentName.INTERNAL_VALUATION_REPORT
          ? [
            editTaskStore.fetchInternalValuationReasonDropdown(loanId, propertyId),
            editTaskStore.fetchInternalValuationCurrentReasonDisplay(loanId, propertyId),
          ]
          : []),
      ]).finally(() => setIsLoading(false));
      if (primaryReason) {
        editTaskStore.onFieldChange('reason', primaryReason);
      }
    }
  }, [open]);

  const currentReason = loanStore.loanDetails?.properties?.find((prop: any) => prop.propertyId === propertyId)?.internalValuationReviewRequiredReason;

  const handleAssignConfirmation = async () => {
    const assigneeId = editTaskStore.form.fields.assigneeId.value;
    const reason = editTaskStore.form.fields.reason.value || currentReason;
    const exception = editTaskStore.form.fields.exception.value;

    if (readOnly && hasComments && !comment) {
      setErrorText('Please enter a comment.');
      return;
    }

    if (exception) {
      // Saved on every property of the loan, replacing previous picks
      await editTaskStore.saveInternalValuationReviewReason(taskId, exception);
    }

    if (taskId) {
      if (readOnly) {
        switch (taskName) {
          case DocumentName.INTERNAL_VALUATION_REPORT:
            editTaskStore.changeLoanInternalValuationReportTaskAssignee(taskId, assigneeId, exception || reason, comment, closeModal, exception);
            break;
          default:
            editTaskStore.changeLoanReadOnlyTaskAssignee(taskId, assigneeId, comment, closeModal);
        }
      } else {
        editTaskStore.changeLoanTaskAssignee(taskId, assigneeId, closeModal);
      }
    } else {
      editTaskStore.saveTaskWithMessage(closeModal, true)
    }
  }

  const handleOnChangeComment = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setErrorText('');
    setComments(e.target.value);
  }

  const closeModal = () => {
    setComments('');
    setErrorText('');
    handleClose();
  }

  const cancelEdition = () => {
    if (!currentReason) {
      editTaskStore.onFieldChange("reason", undefined);
    }
    editTaskStore.onFieldChange("exception", undefined);
    closeModal();
  }

  const extraFields: Partial<Record<DocumentName, JSX.Element>> = {
    [DocumentName.INTERNAL_VALUATION_REPORT]: (
      <Grid item xs={12}>
        <TaskReasonField
          label="Reason"
          value={editTaskStore.form.fields.exception.value || editTaskStore.form.fields.reason.value || editTaskStore.internalValuationCurrentReasonDisplay.join(', ') || currentReason}
          onChange={(_, value) => editTaskStore.onFieldChange("exception", value)}
          disabled={false}
          options={editTaskStore.internalValuationReasonDropdown}
        />
      </Grid>
    ),
  };

  return (
    <>

      <StandardDialog
        classes={{
          paper: classes.dialogPaper,
          container: classes.dialogContainer,
        }}
        open={open}
        title='Task Assignee'
        maxWidth='sm'
        handleClose={cancelEdition}
        dialogContent={
          isLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TaskAssigneeField
                  label={'Assignee'}
                  value={editTaskStore.form.fields.assigneeId.value}
                  onChange={(_: ChangeEvent<{}>, value: string) => editTaskStore.onFieldChange('assigneeId', value)}
                  assigneeOptions={internalAssigneeOptions}
                  disabled={false}
                />
              </Grid>
              {readOnly && taskName && showExtraFields && extraFields[taskName]}
              {readOnly && hasComments && <Grid item xs={12}>
                <TextField
                  type={'text'}
                  variant={'outlined'}
                  label={'Comments: '}
                  value={comment}
                  onChange={handleOnChangeComment}
                  fullWidth
                  multiline={true}
                  required={true}
                  minRows={3}
                  maxRows={3}
                  error={isNotBlank(errorText)}
                  helperText={errorText}
                />
              </Grid>}
            </Grid>
          )
        }
        dialogActions={
          <>
            <Button
              style={{ marginRight: '8px' }}
              onClick={cancelEdition}
              color="primary"
              variant="outlined"
              testId="cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAssignConfirmation}
              color="primary"
              variant="contained"
              testId="confirm"
            >
              Submit
            </Button>
          </>
        }
      />
    </>

  );
});
